// Keep one invoice PUT in flight. Stop queued writes after an error so an invalid
// draft is kept for correction instead of repeatedly submitting it.
export const createInvoiceSaveQueue = () => {
  const pending = [];
  let active = false;
  const drain = () => {
    if (active || !pending.length) return;
    active = true;
    pending.shift()((success = true) => {
      active = false;
      if (!success) pending.length = 0;
      drain();
    });
  };
  return { enqueue: run => { pending.push(run); drain(); } };
};

const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Adopt committed server values only where the draft hasn't changed meanwhile.
// New lines receive their server ID even when the user edited their amount while
// the save was running. Deleted lines stay deleted.
export const mergeSavedInvoiceJson = (sent, current, saved) => {
  if (equal(sent, current)) return saved;
  if (Array.isArray(current)) {
    return current.map(item => {
      if (!item || typeof item !== 'object' || (!item.line_id && item._view_id == null)) return item;
      const match = row => item.line_id ? row.line_id === item.line_id : row._view_id === item._view_id;
      const before = (sent || []).find(match);
      if (!before) return item;
      const committed = (saved || []).find(row => before.line_id
        ? row.line_id === before.line_id : row._view_id === before._view_id);
      return committed ? mergeSavedInvoiceJson(before, item, committed) : item;
    });
  }
  if (current && typeof current === 'object' && sent && typeof sent === 'object') {
    const result = { ...saved };
    for (const key of new Set([...Object.keys(sent), ...Object.keys(current)])) {
      if (equal(sent[key], current[key])) continue;
      if (!(key in current)) delete result[key];
      else result[key] = mergeSavedInvoiceJson(sent[key], current[key], saved?.[key]);
    }
    return result;
  }
  return current;
};

// Translate structural validation failures; never display raw server messages.
export const invoiceSaveErrorKey = error => {
  const code = error?.response?.data?.error;
  if (code === 'invoice_line_identity_invalid') return 'error_invoice_line_identity';
  if (code === 'invoice_changed') return 'error_invoice_save_conflict';
  // XPEN-3786: an amount correction refused after approval or once finalized.
  if (code === 'expense_correction_forbidden') return 'expense_amount_correction.expense_correction_forbidden';
  if (code === 'expense_account_locked') return 'expense_amount_correction.expense_account_locked';
  // XPEN-3802: a report line removed by someone who may not (owner, their associates, or the correction action).
  if (code === 'expense_report_line_removal_forbidden') return 'expense_report_lines.removal_forbidden';
  // XPEN-3795: a report invoice saved back to Pending, or a decided one deleted, without the return action.
  if (code === 'expense_report_status_change_refused') return 'return_to_employee.expense_report_status_change_refused';
  if (code === 'expense_report_action_forbidden') return 'return_to_employee.expense_report_action_forbidden';
  // XPEN-3794: the employee changed a line of their submitted report that was not denied.
  if (code === 'expense_report_submitted') return 'report_edit_lock.submitted';
  return 'internal_saving_error';
};
